import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Fab, IconButton } from '@mui/material'
import PageLoading from '../../../components/PageLoading/PageLoading'
import TableHeader from '../../../components/TableHeader/TableHeader'
import InformationTable from '../../../components/Table/InformationTable'
import { useNotification } from '../../../context/NotificationManager/NotificationManager'
import {
  createExamen,
  deleteExamen,
  getExamenList,
  updateExamen
} from '../../../utils/api/materialDocente'
import { textFilter } from '../../../utils/table'
import ModalConfirmacion from '../../../modals/ModalConfirmacion/ModalConfirmacion'
import { isRolBrain } from '../../../utils/auth'
import ModalEditarExamen from '../../../modals/ModalEditarExamen/ModalEditarExamen'
import ModalNuevoExamen from '../../../modals/ModalNuevoExamen/ModalNuevoExamen'
import ModalAsignaturas from '../../../modals/ModalAsignaturas/ModalAsignaturas'
import ModalPdfViewer from '../../../modals/ModalPdfViewer/ModalPdfViewer'
import AsignaturaSelector from '../../../components/form/AsignaturaSelector/AsignaturaSelector'
import { pluralize } from '../../../utils/string'
import css from './Examenes.module.css'

const Examenes = () => {
  const queryClient = useQueryClient()
  const notification = useNotification()
  const [search, setSearch] = useState('')
  const [examenSeleccionado, setExamenSeleccionado] = useState(null)
  const [isOpenEditarExamen, setIsOpenEditarExamen] = useState(false)
  const [isOpenEliminarExamen, setIsOpenEliminarExamen] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isOpenAsignaturasModal, setIsOpenAsignaturasModal] = useState(false)
  const [isOpenPdfModal, setIsOpenPdfModal] = useState(false)
  const [selectedAsignatura, setSelectedAsignatura] = useState(null)

  const accesoBrain = isRolBrain()

  const { isLoading, data: examenList = [] } = useQuery({
    queryKey: ['profes', 'material', 'examen'],
    queryFn: () =>
      getExamenList().catch(err => {
        notification.error({
          title: 'Error al recuperar los exámenes',
          content: err
        })
        return []
      })
  })

  const { isPending: isExamenCreating, mutate: crearExamen } = useMutation({
    mutationFn: createExamen,
    onSuccess: () => {
      notification.success({
        title: 'Examen enviado',
        content: 'Se ha enviado el examen'
      })
      queryClient.invalidateQueries({
        queryKey: ['profes', 'material', 'examen']
      })
      setIsModalOpen(false)
    },
    onError: err => {
      notification.error({ title: 'Error al enviar el examen', content: err })
    }
  })

  const { isPending: isExamenUpdating, mutate: actualizarExamen } = useMutation(
    {
      mutationFn: updateExamen,
      onSuccess: () => {
        queryClient.invalidateQueries(['profes', 'material', 'examen'])
        notification.success({
          title: 'Examen actualizado',
          content: 'Datos modificados correctamente'
        })
        setIsOpenEditarExamen(false)
      },
      onError: err => {
        notification.error({
          title: 'Error actualizando el examen',
          content: err
        })
      }
    }
  )

  const { isPending: isExamenDeleting, mutate: borrarExamen } = useMutation({
    mutationFn: deleteExamen,
    onSuccess: () => {
      queryClient.invalidateQueries(['profes', 'material', 'examen'])
      notification.success({
        title: 'Examen borrado',
        content: 'Se ha borrado el examen correctamente'
      })
      setExamenSeleccionado(null)
      setIsOpenEliminarExamen(false)
      closeModal()
    },
    onError: err => {
      notification.error({ title: 'Error al borrar el examen', content: err })
    }
  })

  const handleNuevoExamen = ({ nombre, tipo, asignaturas, archivo }) => {
    if (isExamenCreating) return
    crearExamen({ nombre, tipo, asignaturas, archivo })
  }

  const handleUpdateExamen = updatedExamen => {
    if (isExamenUpdating) return
    actualizarExamen(updatedExamen)
  }

  const handleDeleteExamen = () => {
    if (isExamenDeleting) return
    borrarExamen(examenSeleccionado.id)
  }

  const filterSearch = examen => {
    const matchesSearch = textFilter({
      object: examen,
      fields: ['nombre', 'tipo'],
      search: search
    })

    const matchesAsignatura =
      !selectedAsignatura ||
      (examen.asignaturas &&
        examen.asignaturas.some(
          asignatura => asignatura.id === selectedAsignatura.id
        ))

    return matchesSearch && matchesAsignatura
  }

  const openModal = examen => {
    setExamenSeleccionado(examen)
    setIsOpenEditarExamen(true)
  }

  const closeModal = () => {
    setIsOpenEditarExamen(false)
    setExamenSeleccionado(null)
  }

  return (
    <PageLoading isLoading={isLoading}>
      <TableHeader
        title='Exámenes'
        search={search}
        onSearchChange={e => setSearch(e.target.value)}
        actions={
          <AsignaturaSelector
            label='Asignatura'
            value={selectedAsignatura?.id || null}
            onChange={(_event, newValue) => setSelectedAsignatura(newValue)}
            className={css.asignaturaFilter}
          />
        }
      />
      <InformationTable
        details={[
          { title: 'Nombre', key: 'nombre', sortKey: 'nombreExamen' },
          { title: 'Tipo', key: 'tipo' },
          {
            title: 'Asignaturas',
            key: 'asignaturas',
            sortKey: 'totalAsignaturas'
          },
          { title: 'Opciones', key: 'opciones', align: 'center' }
        ]}
        data={examenList.filter(filterSearch).map(examen => ({
          nombre: (
            <span
              className={css.link}
              onClick={() => {
                setExamenSeleccionado(examen)
                setIsOpenPdfModal(true)
              }}
            >
              {examen.nombre || '--'}
            </span>
          ),
          nombreExamen: examen.nombre,
          tipo: examen.tipo.replace('_', ' ') || '--',
          totalAsignaturas: examen.asignaturas ? examen.asignaturas.length : 0,
          asignaturas:
            examen.asignaturas && examen.asignaturas.length > 0 ? (
              <span
                className={css.asignaturasCount}
                onClick={() => {
                  setExamenSeleccionado(examen)
                  setIsOpenAsignaturasModal(true)
                }}
              >
                {examen.asignaturas.length}{' '}
                {pluralize('asignatura', examen.asignaturas.length)}
              </span>
            ) : (
              <span className={css.sinAsignaturas}>Sin asignaturas</span>
            ),
          opciones: (
            <>
              <IconButton
                size='small'
                color='secondary'
                disabled={!accesoBrain}
                onClick={() => openModal(examen)}
              >
                <i className='material-icons'>create</i>
              </IconButton>
              <IconButton
                size='small'
                disabled={!accesoBrain}
                color='error'
                onClick={() => {
                  setExamenSeleccionado(examen)
                  setIsOpenEliminarExamen(true)
                }}
              >
                <i className='material-icons'>delete</i>
              </IconButton>
            </>
          )
        }))}
        isFetching={isLoading}
      />
      <ModalNuevoExamen
        open={isModalOpen}
        onSubmit={handleNuevoExamen}
        onClose={() => setIsModalOpen(false)}
        isLoading={isExamenCreating}
      />
      <ModalEditarExamen
        open={isOpenEditarExamen}
        examen={examenSeleccionado}
        onClose={closeModal}
        onSubmit={handleUpdateExamen}
      />
      <ModalConfirmacion
        title='¿Seguro que quieres borrar el examen?'
        content='Al borrar el examen, dejará de estar disponible en el área de profesores, así como para la selección de material que se realiza en Classfy.'
        open={isOpenEliminarExamen}
        onClose={() => setIsOpenEliminarExamen(false)}
        onClick={handleDeleteExamen}
      />
      <ModalAsignaturas
        open={isOpenAsignaturasModal}
        onClose={() => setIsOpenAsignaturasModal(false)}
        name={examenSeleccionado?.nombre || ''}
        asignaturas={examenSeleccionado?.asignaturas || []}
      />
      <ModalPdfViewer
        open={isOpenPdfModal}
        onClose={() => setIsOpenPdfModal(false)}
        title={examenSeleccionado?.nombre || ''}
        pdfUrl={examenSeleccionado?.url || ''}
      />
      {accesoBrain && (
        <Fab
          className={css.nuevo}
          disabled={isExamenCreating}
          onClick={() => setIsModalOpen(true)}
          size='medium'
          color='primary'
          aria-label='add'
        >
          <i className='material-icons'>add</i>
        </Fab>
      )}
    </PageLoading>
  )
}

export default Examenes
